import { Component, h, Prop } from '@stencil/core';
import { t } from '../../../redux/utils';
import { PickerDate } from '../../rtr-article-details/utils/types';
import { format, intervalToDuration, isSameDay, parse } from 'date-fns';
import { dateNoTime, dateTime, noDateNoTime, noDateTime } from '../../rtr-article-details/utils/picker';

@Component({
  tag: 'rtr-date-info',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RtrDateInfo {
  @Prop() pickerDate: PickerDate;

  get daysRange() {
    if (!(this.pickerDate.dropOffDate || this.pickerDate.suspectedDropOffDate)) {
      return 0;
    }

    return (
      intervalToDuration({
        start: this.pickerDate.pickUpDate,
        end: this.pickerDate.dropOffDate || this.pickerDate.suspectedDropOffDate,
      }).days + 1
    );
  }

  get hoursRange() {
    return intervalToDuration({
      start: parse(this.pickerDate.pickUpTime, 'HH:mm', new Date()),
      end: parse(this.pickerDate.dropOffTime, 'HH:mm', new Date()),
    }).hours;
  }

  get title() {
    if (!this.pickerDate.isOrder) {
      return t('details.availability');
    }

    return t('details.booking');
  }

  formattedDateTime = (date: Date): string => {
    if (!date) return '';

    return format(date, 'PP, p');
  };

  formattedDate = (date: Date): string => {
    if (!date) return '';

    return format(date, 'PP');
  };

  formattedTime = (date: Date): string => {
    if (!date) return '';

    return format(date, 'p');
  };

  get selectADateText() {
    return t('details.selectADate');
  }

  get selectPickupDateText() {
    return t('details.selectPickupDate');
  }

  get selectReturnDateText() {
    return t('details.selectReturnDate');
  }

  get selectPickupTimeText() {
    return t('details.selectPickupTime');
  }

  get selectReturnDateOrPickupTimeText() {
    return t('details.selectReturnDateOrPickupTime');
  }

  get selectReturnTimeText() {
    return t('details.selectReturnTime');
  }

  get info() {
    if (dateTime(this.pickerDate)) {
      if (!this.pickerDate.pickUpDate) {
        return <span class="RDIWarning">{this.selectPickupDateText}</span>;
      }
      if (!this.pickerDate.dropOffDate) {
        if (!this.pickerDate.pickUpTime) {
          return <span class="RDIWarning">{this.selectReturnDateOrPickupTimeText}</span>;
        } else {
          return <span class="RDIWarning">{this.selectReturnDateText}</span>;
        }
      }

      if (!this.pickerDate.pickUpTime) {
        return <span class="RDIWarning">{this.selectPickupTimeText}</span>;
      }

      if (!this.pickerDate.dropOffTime) {
        return <span class="RDIWarning">{this.selectReturnTimeText}</span>;
      }

      const pickUpTimeDate = parse(this.pickerDate.pickUpTime, 'HH:mm', this.pickerDate.pickUpDate);
      const dropOffTimeDate =
        this.pickerDate.dropOffDate || this.pickerDate.suspectedDropOffDate
          ? parse(this.pickerDate.dropOffTime, 'HH:mm', this.pickerDate.dropOffDate || this.pickerDate.suspectedDropOffDate)
          : null;

      if (isSameDay(pickUpTimeDate, dropOffTimeDate)) {
        return (
          <span>
            {this.formattedDateTime(pickUpTimeDate)} → {this.formattedTime(dropOffTimeDate)}
          </span>
        );
      }

      return (
        <span>
          {this.formattedDateTime(pickUpTimeDate)} → {this.formattedDateTime(dropOffTimeDate)}
        </span>
      );
    } else if (dateNoTime(this.pickerDate)) {
      if (!this.pickerDate.pickUpDate) {
        return <span class="RDIWarning">{this.selectPickupDateText}</span>;
      }

      if (!this.pickerDate.suspectedDropOffDate && !this.pickerDate.dropOffDate) {
        return <span class="RDIWarning">{this.selectReturnDateText}</span>;
      }

      if (isSameDay(this.pickerDate.pickUpDate, this.pickerDate.dropOffDate || this.pickerDate.suspectedDropOffDate)) {
        return <span>{this.formattedDate(this.pickerDate.pickUpDate)}</span>;
      }

      return (
        <span>
          {this.formattedDate(this.pickerDate.pickUpDate)} → {this.formattedDate(this.pickerDate.dropOffDate)}
        </span>
      );
    } else if (noDateTime(this.pickerDate)) {
      if (!this.pickerDate.pickUpDate) {
        return <span class="RDIWarning">{this.selectPickupDateText}</span>;
      }

      if (!this.pickerDate.pickUpTime) {
        return <span class="RDIWarning">{this.selectPickupTimeText}</span>;
      }

      if (!this.pickerDate.dropOffTime) {
        return <span class="RDIWarning">{this.selectReturnTimeText}</span>;
      }

      const pickUpTimeDate = parse(this.pickerDate.pickUpTime, 'HH:mm', this.pickerDate.pickUpDate);
      const dropOffTimeDate = parse(this.pickerDate.dropOffTime, 'HH:mm', this.pickerDate.pickUpDate);

      return (
        <span>
          {this.formattedDateTime(pickUpTimeDate)} → {this.formattedTime(dropOffTimeDate)}
        </span>
      );
    } else if (noDateNoTime(this.pickerDate)) {
      if (!this.pickerDate.pickUpDate) {
        return <span class="RDIWarning">{this.selectADateText}</span>;
      }

      return this.formattedDate(this.pickerDate.pickUpDate);
    }
  }

  render() {
    return (
      <div class="RDIContainer">
        <span class="RDITitle">
          <span class="Title">{this.title}</span>
        </span>
        <rentware-spacer size="S" />
        <span>{this.info}</span>
      </div>
    );
  }
}
